import { useDispatch } from 'react-redux';
import { SET_MODAL, DEL_MODAL } from 'constants';
import {
  GET_SCRIPT_ENVS,
  GET_SCRIPT_STRUCTURE,
  CREATE_SCRIPT_OBJECTS,
  UPDATE_SCRIPT_OBJECTS,
  REMOVE_SCRIPT_OBJECTS,
  DEPLOY_SCRIPT,
} from '@control-front-end/common/constants/scripts';
import {
  GET_ACTOR,
  REMOVE_ACTOR,
} from '@control-front-end/common/constants/graphActors';
import AppUtils from '@control-front-end/utils/utils';

const DEFAULT_PAGE_CONTENT = [
  {
    title: 'config',
    type: 'application/json',
    source: JSON.stringify({
      grid: {
        type: 'one_column',
        header: {
          class: 'default',
        },
        components: {
          center: ['info'],
        },
      },
      forms: [
        {
          id: 'info',
          sections: [
            {
              content: [
                {
                  id: 'label-1',
                  class: 'label',
                  value: 'Page title',
                },
                {
                  id: 'key-1',
                  class: 'edit',
                  type: 'text',
                  title: 'Key',
                },
                {
                  class: 'button',
                  type: 'default',
                  title: 'Button label',
                },
              ],
            },
          ],
        },
      ],
    }),
    objType: 'file',
  },
  {
    title: 'locale',
    type: 'application/json',
    source: '{}',
    objType: 'file',
  },
];

/**
 * Ф-ции по работе со структурой CDU приложения
 */
const useScriptContentActions = ({
  accId,
  script,
  content,
  setActiveFile,
  setActiveScript,
}) => {
  const dispatch = useDispatch();
  const scriptId = script ? script.id : null;

  /**
   * Получить приложение CDU
   */
  const handleGetScriptActor = ({ id, callback }) => {
    if (!id) return;
    dispatch({
      type: GET_ACTOR.REQUEST,
      payload: { id },
      callback,
    });
  };

  /**
   * Получить приложение CDU
   */
  const handleGetScriptEnvs = ({ id, callback }) => {
    if (!id) return;
    dispatch({
      type: GET_SCRIPT_ENVS.REQUEST,
      payload: id,
      callback,
    });
  };

  /**
   * Получить структуру файлов приложения CDU
   */
  const handleGetStructure = ({ envId, callback }) => {
    dispatch({
      type: GET_SCRIPT_STRUCTURE.REQUEST,
      payload: { scriptId, envId },
      callback,
    });
  };

  /**
   * Создать объекты в структуре приложения
   */
  const createScriptObject = ({ content: body, callback }) => {
    dispatch({
      type: CREATE_SCRIPT_OBJECTS.REQUEST,
      payload: { scriptId, body },
      callback,
    });
  };

  /**
   * Создать новую страницу в приложении
   */
  const handleCreatePage = () => {
    const rootPagesFolder = content.children.find(
      (i) => i.isSystem && i.title === 'pages'
    );
    const nextPageNumber = rootPagesFolder.children.length + 1;
    const newPageFolder = [
      {
        folderId: rootPagesFolder.id,
        title: `page_${nextPageNumber}`,
        objType: 'folder',
      },
    ];
    createScriptObject({
      content: newPageFolder,
      callback: (data) => {
        const [pageFolder] = data;
        createScriptObject({
          content: DEFAULT_PAGE_CONTENT.map((file) => ({
            ...file,
            folderId: pageFolder.id,
          })),
          callback: (pageFiles) => {
            const pageConfig = pageFiles.find((i) => i.title === 'config');
            setActiveFile(pageConfig);
          },
        });
      },
    });
  };

  /**
   * Создать дубликат объекта
   */
  const handleDuplicate = (obj) => {
    const folderId = obj.folderId || obj.parentId;
    const copyTitle = `${obj.title}(copy)`;
    createScriptObject({
      content: [{ ...obj, folderId, title: copyTitle }],
      callback: (data) => {
        if (obj.objType === 'folder' && obj.children && obj.children.length) {
          const [newFolder] = data;
          createScriptObject({
            content: obj.children.map((childObj) => ({
              ...childObj,
              folderId: newFolder.id,
            })),
          });
        }
      },
    });
  };

  /**
   * Создать новый объект в структуре приложения (в папке definitons)
   */
  const handleCreateObject = ({ folderId, objType }) => {
    const obj = { folderId, objType, title: `new_${objType}` };
    if (objType === 'file') {
      obj.type = 'application/json';
      obj.source = JSON.stringify({});
    }
    createScriptObject({ content: [obj] });
  };

  /**
   * Обновить объекты в приложении
   */
  const handleChangeFile = ({ obj, callback }) => {
    const { id, title, isStatic, objType, source, type } = obj;
    const folderId = obj.folderId || obj.parentId;
    const body = [{ id, folderId, title, objType, type, source, isStatic }];
    dispatch({
      type: UPDATE_SCRIPT_OBJECTS.REQUEST,
      payload: { scriptId, body },
      callback,
    });
  };

  /**
   * Удалить объекты
   */
  const handleRemoveFile = (obj) => {
    const { id, objType } = obj;
    const folderId = obj.folderId || obj.parentId;
    dispatch({
      type: REMOVE_SCRIPT_OBJECTS.REQUEST,
      payload: { scriptId, folderId, body: [{ id, objType }] },
    });
  };

  /**
   * Удалить приложение CDU
   */
  const removeScript = () => {
    dispatch({
      type: REMOVE_ACTOR.REQUEST,
      payload: { id: scriptId, manageLayer: false },
      callback: () => {
        document.location.href = AppUtils.makeUrl(
          `/actors_graph/${accId}/scripts`
        );
      },
    });
  };

  /**
   * Подтверждение удаления приложения через модальное окно
   */
  const handleRemoveScript = () => {
    dispatch({
      type: SET_MODAL,
      payload: {
        name: 'InfoModal',
        data: {
          title: 'removeScript',
          text: 'removeScriptInfo',
          buttons: [
            {
              type: 'error',
              label: 'OK',
              onClick: () => {
                removeScript();
                dispatch({ type: DEL_MODAL });
              },
            },
          ],
        },
      },
    });
  };

  /**
   * Выполнить деплой на продакшн
   */
  const deployScript = () => {
    const develop = script.envs.find((i) => i.title === 'develop');
    const production = script.envs.find((i) => i.title === 'production');
    dispatch({
      type: DEPLOY_SCRIPT.REQUEST,
      payload: {
        scriptId,
        sourceEnvId: develop.id,
        targetEnvId: production.id,
      },
    });
  };

  /**
   * Подтверждение деплоя на продакшн через модальное окно
   */
  const handleDeploy = () => {
    dispatch({
      type: SET_MODAL,
      payload: {
        name: 'InfoModal',
        data: {
          title: 'deployScript',
          text: 'deployScriptInfo',
          buttons: [
            {
              type: 'default',
              label: 'OK',
              onClick: () => {
                deployScript();
                dispatch({ type: DEL_MODAL });
              },
            },
          ],
        },
      },
    });
  };

  /**
   * Открыть модалку для управления доступом к приложению CDU
   */
  const handleShareScript = () => {
    dispatch({
      type: SET_MODAL,
      payload: {
        name: 'ManageAccessRules',
        data: {
          objType: 'actor',
          objId: scriptId,
          rules: script.access,
          ownerId: script.user.id,
        },
        callback: (access) => setActiveScript({ ...script, access }),
      },
    });
  };

  return {
    handleGetScriptActor,
    handleGetScriptEnvs,
    handleGetStructure,
    handleCreatePage,
    handleCreateObject,
    handleChangeFile,
    handleRemoveFile,
    handleShareScript,
    handleRemoveScript,
    handleDeploy,
    handleDuplicate,
  };
};

export default useScriptContentActions;
